import React, {ReactNode, useEffect, useState} from "react";
import {useModal} from "@common/hooks/use-modal";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import {animated, useTransition} from "@react-spring/web";
import ReactDOM from "react-dom";
import {ModalPosition} from "@common/models/enums/modal-positions.enum";
import {useMediaQuery} from "react-responsive";
import {classNames} from "@utils/class-names";
import {getPositionCSS} from "../helpers/get-position-css";

interface AppModalProps {
    id: ModalIds;
    children?: ReactNode;
    disableScroll?: boolean;
    appendTo?: string | React.RefObject<HTMLElement> | null; // Ref or string
    zIndex?: number;
    mobilePosition?: ModalPosition;
    desktopPosition?: ModalPosition;
    classModal?: string;
    breakPointDesktop?: number;
    forceFixed?: boolean;
}

const AppModal: React.FC<AppModalProps> = (props: AppModalProps) => {
    const {
        id,
        children,
        disableScroll = false,
        appendTo,
        zIndex,
        mobilePosition = ModalPosition.BOTTOM_CENTER,
        desktopPosition = ModalPosition.CENTER,
        classModal,
        breakPointDesktop = 768,
        forceFixed = false,
    } = props;
    const {isOpenModal} = useModal();
    const isModalOpen = isOpenModal(id);
    const [backDropPosition, setBackDropPosition] = useState({scrollOffset: "0"});
    const isDesktop = useMediaQuery({minWidth: breakPointDesktop})

    const [currentModalRoot, setCurrentModalRoot] = useState<HTMLElement | null>(document.body);

    useEffect(() => {
        if (typeof appendTo === "string") {
            const element = document.getElementById(appendTo) || document.getElementsByName(appendTo)[0] as HTMLElement;
            if (element && element !== currentModalRoot) {
                setCurrentModalRoot(element);
            }
        } else if (appendTo && "current" in appendTo && appendTo.current && appendTo.current !== currentModalRoot) {
            setCurrentModalRoot(appendTo.current);
        }
    }, [appendTo, currentModalRoot]);

    const transitionsBackdrop = useTransition(isModalOpen, {
        from: {opacity: 0},
        enter: {opacity: 0.75},
        leave: {opacity: 0},
        config: {tension: 200, friction: 30},
    });

    useEffect(() => {
        const preventScroll = (event: Event) => {
            event.preventDefault();
        };

        if (isModalOpen && disableScroll) {
            window.addEventListener("wheel", preventScroll, {passive: false});
            window.addEventListener("touchmove", preventScroll, {passive: false});
        } else {
            window.removeEventListener("wheel", preventScroll);
            window.removeEventListener("touchmove", preventScroll);
        }

        return () => {
            window.removeEventListener("wheel", preventScroll);
            window.removeEventListener("touchmove", preventScroll);
        };
    }, [isModalOpen, disableScroll]);

    useEffect(() => {
        if (isModalOpen) {
            const updateBackdropPosition = () => {
                const scrollOffset = currentModalRoot === document.body ? window.scrollY : currentModalRoot?.scrollTop;
                setBackDropPosition({scrollOffset: `${scrollOffset || 0}`})
            };

            updateBackdropPosition();
            window.addEventListener("scroll", updateBackdropPosition);

            return () => {
                window.removeEventListener("scroll", updateBackdropPosition);
            };
        }
    }, [isModalOpen, appendTo]);

    if (!isModalOpen) return null;

    const modalContent = (
        <>
            {transitionsBackdrop(
                (styles, item) =>
                    item && (
                        <animated.div
                            style={{
                                ...styles,
                                top: `${backDropPosition.scrollOffset}px`, // dopasowuje top do przewinięcia
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: zIndex ?? 10,
                            }}
                            className="bg-black/30 inset-0 h-full absolute"
                        ></animated.div>
                    )
            )}
            <div
                style={{
                    zIndex: zIndex ? (zIndex + 1) : 11,
                    ...getPositionCSS(isDesktop ? desktopPosition : mobilePosition),
                }}
                className={classNames(
                    "w-full md:w-fit flex items-center justify-center",
                    forceFixed ? "fixed" : "absolute"
                )}
            >
                <div
                    className={classNames(
                        "relative flex gap-4 lg:max-w-lg md:max-w-md w-full p-3 md:w-fit justify-center",
                        classModal,
                    )}
                >
                    {children}
                </div>
            </div>
        </>
    );

    return ReactDOM.createPortal(modalContent, currentModalRoot!);
};

export default AppModal;
